import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Introduction, MessageDraft } from '@/types/ai';

export function useDraftIntroduction() {
  return useMutation({
    mutationFn: async (params: {
      match_id: string;
      tone?: string;
      length?: string;
      include_mutual?: boolean;
      custom_instructions?: string;
    }): Promise<MessageDraft & { member_1: { user_id: string; full_name: string }; member_2: { user_id: string; full_name: string } }> => {
      const { data, error } = await supabase.functions.invoke('draft-introduction', {
        body: params,
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
  });
}

export function useSaveIntroduction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      matchId,
      subject,
      body,
      sentVia,
    }: {
      matchId: string;
      subject: string;
      body: string;
      sentVia?: 'clipboard' | 'email' | 'in_app';
    }) => {
      const { data: { user } } = await supabase.auth.getUser();

      // Insert introduction
      const { error: introError } = await supabase
        .from('introductions')
        .insert({
          match_id: matchId,
          subject,
          body,
          sent_via: sentVia || 'clipboard',
          drafted_by: user?.id || null,
        });

      if (introError) throw introError;

      // Update match status to 'introduced'
      const { error: matchError } = await supabase
        .from('matches')
        .update({
          status: 'introduced',
          introduced_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', matchId);

      if (matchError) throw matchError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-matches'] });
      queryClient.invalidateQueries({ queryKey: ['introductions'] });
    },
  });
}

export function useIntroductions(matchId?: string) {
  return useQuery({
    queryKey: ['introductions', matchId],
    queryFn: async () => {
      let query = supabase
        .from('introductions')
        .select('*')
        .order('created_at', { ascending: false });

      if (matchId) {
        query = query.eq('match_id', matchId);
      }

      const { data, error } = await query.limit(50);
      if (error) throw error;
      return (data || []) as Introduction[];
    },
  });
}
